import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { AgGridReact } from 'ag-grid-react'
import { agConfigSideBar, tableCellStyle } from '../../../../utils/AgGridConfig'
import {
  ChartRef,
  ColDef,
  ChartOptionsChanged,
  ChartRangeSelectionChanged,
  ChartDestroyed,
  SideBarDef,
} from 'ag-grid-community'
import { DynamicTableDataInputType } from '../wrappers/DynamicTablesPageWrapper'
import { DynamicAction } from './IndividualTablePage'
import Toast, { ToastType } from '../../../../utils/toast'
import { Modal } from 'react-bootstrap'
import { Trash } from 'lucide-react'
import { HStack } from '../../../../utils/Stack'
import { CustomFilterComponent } from './CustomFilterComponent'
import _ from 'lodash'
import { DynamicTableFilterInput, GetDynamicFilterInput } from '../../../../generated/graphql'
import { formatNumber, getCurrency } from '../../../../utils/CurrencyFormatter'
import { currencies } from '../../../../utils/CurrencyList'

type DynamicTableProps = {
  tableId: number
  data: any[]
  savedCharts: any[]
  name: string
  tableData: DynamicTableDataInputType
  handleChartData: Function
  handleUpdate: Function
  index: number
  chartData: any[]
  setChartData: Function
  filterData?: AvailableTableFilters[]
  saveFilter: Function
  removeFilter: Function
}

const dynamicSideBar: SideBarDef = {
  position: 'left' as const,
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      minWidth: 200,
      width: 250,
      maxWidth: 300,
      toolPanelParams: {
        hide: true,
        suppressRowGroups: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressValues: true,
      },
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
      minWidth: 200,
      maxWidth: 300,
      width: 250,
    },
  ],
}

export interface AvailableTableFilters {
  id: string
  filterName: string
  filterState: any
  tableName: string
  isSelected: boolean
}

// Custom Tooltip Component
const CustomTooltip = (props: any) => {
  const columnKey = props.colDef.field
  const match = columnKey.match(/\((.*?)\)/)

  if (!match) return null

  const currency = match[1]
  const primaryValue = props.data[`${columnKey}`]
  const secondaryValue = props.data[`${columnKey}_secondary`]

  let columnCurrency: string | null = null
  let secondaryColumnCurrency: string | null = null

  const matchSecondary = props.data[`${columnKey}_secondary_label`]?.match(/\((.*?)\)/)
  if (match && currencies.includes(match[1])) {
    columnCurrency = match[1]
  }
  if (matchSecondary && currencies.includes(matchSecondary[1])) {
    secondaryColumnCurrency = matchSecondary[1]
  }
  const currencyFormat = getCurrency(columnCurrency || 'INR - ₹')
  const secondaryCurrencyFormat = getCurrency(secondaryColumnCurrency || 'INR - ₹')

  return typeof primaryValue === 'number' || typeof secondaryValue === 'number' ? (
    <div
      style={{
        backgroundColor: '#ffffff',
        color: '#000000',
        border: '1px solid #e5e5e5',
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        maxWidth: '300px',
        lineHeight: '1.4',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}
    >
      {primaryValue && typeof primaryValue === 'number' ? (
        <div className='fs-6'>
          {primaryValue?.toLocaleString(currencyFormat || 'en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ) : null}

      {secondaryValue && typeof secondaryValue === 'number' ? (
        <div className='fs-8 text-gray-500'>
          {secondaryValue?.toLocaleString(secondaryCurrencyFormat || 'en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ) : null}
    </div>
  ) : null
}

export function DynamicDataAGTable({
  tableId,
  data,
  savedCharts,
  name,
  tableData,
  handleChartData,
  handleUpdate,
  index,
  chartData,
  setChartData,
  filterData,
  saveFilter,
  removeFilter,
}: DynamicTableProps) {
  const gridRef = useRef<any>(null)
  const [rowData, setRowData] = useState<any[]>()
  const { primary, secondary, primaryLabel, secondaryLabel, tableTitle } =
    tableData.indexData[index] || tableData.indexData[0]
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([])
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), [])
  const gridStyle = useMemo(() => ({ height: '500px', width: '100%' }), [])
  const [isColumnOpen, setIsColumnOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [dataState, setDataState] = useState<DynamicAction>(DynamicAction.ADD)
  const [currentChartData, setCurrentChartData] = useState<any[]>(savedCharts)
  const [isExpanded, setIsExpanded] = useState(true)
  const [showClose, setShowClose] = useState(false)
  const [currentChartRef, setCurrentChartRef] = useState<any>()
  const [eeParent, setEEParent] = useState<any>()
  const [eeWrapper, setEEWrapper] = useState<any>()
  const [searchedValue, setSearchedValue] = useState('')
  const [filteredRowCount, setFilteredRowCount] = useState(0)
  const tableIndex = index

  const onFilterSelected = (filterItem: AvailableTableFilters | null) => {
    gridRef?.current?.api.setFilterModel(filterItem?.filterState)

    // Open filter panel when a custom filter is applied
    if (filterItem?.filterState && Object.keys(filterItem.filterState).length > 0) {
      gridRef?.current?.api.openToolPanel('filters')
      setIsFilterOpen(true)
      setIsColumnOpen(false)

      // Expand only the active filter columns with multiple attempts
      setTimeout(() => {
        const activeFilterColumns = Object.keys(filterItem.filterState)

        const expandFilterRecursively = (element: Element) => {
          // Find all title bars within this element
          const titleBars = element.querySelectorAll('.ag-filter-toolpanel-group-title-bar')
          titleBars.forEach((titleBar) => {
            const groupContainer = titleBar.parentElement?.querySelector(
              '.ag-filter-toolpanel-group-container'
            )
            if (groupContainer) {
              const isExpanded = !groupContainer.classList.contains('ag-hidden')
              if (!isExpanded) {
                ;(titleBar as HTMLElement).click()

                // After expanding, check for nested filters and expand them too
                setTimeout(() => {
                  expandFilterRecursively(groupContainer)
                }, 100)
              }
            }
          })

          // Also look for multi-column filter sub-menus
          const subMenus = element.querySelectorAll(
            '.ag-filter-toolpanel-group-container .ag-filter-toolpanel-group-container'
          )
          subMenus.forEach((subMenu) => {
            expandFilterRecursively(subMenu)
          })
        }

        activeFilterColumns.forEach((colId) => {
          // Method 1: Look for AG-Grid filter panel items
          const filterPanelItems = document.querySelectorAll('.ag-filter-toolpanel-instance')
          filterPanelItems.forEach((item) => {
            const colAttribute = item.getAttribute('col-id') || item.getAttribute('data-col-id')
            if (colAttribute === colId) {
              const titleBar = item.querySelector('.ag-filter-toolpanel-group-title-bar')
              const groupContainer = item.querySelector('.ag-filter-toolpanel-group-container')

              if (titleBar && groupContainer) {
                // Check if already expanded
                const isExpanded = !groupContainer.classList.contains('ag-hidden')
                if (!isExpanded) {
                  ;(titleBar as HTMLElement).click()

                  // For multi-column filters, expand all nested levels
                  setTimeout(() => {
                    expandFilterRecursively(item)
                  }, 200)
                }
              }
            }
          })

          // Method 2: Direct DOM query with column field
          const filterElement = document?.querySelector(
            `[col-id="${colId}"] .ag-filter-toolpanel-group-title-bar`
          )
          if (filterElement) {
            ;(filterElement as HTMLElement).click()

            // Expand nested filters
            setTimeout(() => {
              const parentElement = filterElement.closest('.ag-filter-toolpanel-instance')
              if (parentElement) {
                expandFilterRecursively(parentElement)
              }
            }, 200)
          }

          // Method 3: Look for elements containing the column name and expand recursively
          const allTitleBars = document.querySelectorAll('.ag-filter-toolpanel-group-title-bar')
          allTitleBars.forEach((titleBar) => {
            const titleText = titleBar.textContent?.trim()
            if (titleText && titleText.includes(colId)) {
              ;(titleBar as HTMLElement).click()

              // Expand all nested levels
              setTimeout(() => {
                const parentInstance = titleBar.closest('.ag-filter-toolpanel-instance')
                if (parentInstance) {
                  expandFilterRecursively(parentInstance)
                }
              }, 200)
            }
          })
        })
      }, 500) // Increased timeout
    } else {
      // When filter is removed/reset, collapse all expanded filter options
      setTimeout(() => {
        const filterPanelItems = document.querySelectorAll('.ag-filter-toolpanel-instance')
        filterPanelItems.forEach((item) => {
          const titleBar = item.querySelector('.ag-filter-toolpanel-group-title-bar')
          const groupContainer = item.querySelector('.ag-filter-toolpanel-group-container')

          if (titleBar && groupContainer) {
            // Check if currently expanded
            const isExpanded = !groupContainer.classList.contains('ag-hidden')
            if (isExpanded) {
              // Click to collapse
              ;(titleBar as HTMLElement).click()
            }
          }
        })

        // Alternative method: collapse all title bars that are expanded
        const allTitleBars = document.querySelectorAll('.ag-filter-toolpanel-group-title-bar')
        allTitleBars.forEach((titleBar) => {
          const parentElement = titleBar.parentElement
          if (parentElement) {
            const groupContainer = parentElement.querySelector(
              '.ag-filter-toolpanel-group-container'
            )
            if (groupContainer && !groupContainer.classList.contains('ag-hidden')) {
              // This filter is expanded, collapse it
              ;(titleBar as HTMLElement).click()
            }
          }
        })
      }, 200)
    }
  }

  function isNumber(value: any) {
    return Number(value)
  }
  const handleToggle = (event: any) => {
    setIsExpanded(event.target.open)
  }

  function isDate(value: any): boolean {
    const date = new Date(value)
    return !isNaN(date.getTime())
  }

  function CustomRowData(data: any) {
    const currentColumnName = data?.column?.colId
    let currentKey = data.colDef.field
    let columnCurrency: string | null = null
    let secondaryColumnCurrency: string | null = null

    const match = currentKey.match(/\((.*?)\)/)
    const matchSecondary = data.data[`${currentKey}_secondary_label`]?.match(/\((.*?)\)/)
    if (match && currencies.includes(match[1])) {
      columnCurrency = match[1]
    }
    if (matchSecondary && currencies.includes(matchSecondary[1])) {
      secondaryColumnCurrency = matchSecondary[1]
    }

    const currencyFormat = getCurrency(columnCurrency || 'INR - ₹')
    const secondaryCurrencyFormat = getCurrency(secondaryColumnCurrency || 'INR - ₹')

    const primaryValue = columnCurrency
      ? formatNumber({
          value: data.data[currentKey],
          format: currencyFormat,
        })
      : data.data[currentKey]
    const secondaryValue = secondaryColumnCurrency
      ? formatNumber({
          value: data.data[`${currentKey}_secondary`],
          format: secondaryCurrencyFormat,
        })
      : data.data[`${currentKey}_secondary`]

    return (
      <div className={`d-block fs-base ${data.data[`${currentKey}_secondary`] ? '' : ' pt-2'}`}>
        <h6 className='mb-0'>
          {currentKey === currentColumnName &&
          data.data[currentColumnName + '-links'] &&
          data.data[currentColumnName + '-links'] !== '' ? (
            <MyLink url={data.data[`${currentColumnName}-links`]}>{primaryValue}</MyLink>
          ) : (
            primaryValue
          )}
        </h6>

        {data.data[`${currentKey}_secondary`] && (
          <p className='text-muted mb-0 fs-8'>{secondaryValue}</p>
        )}
      </div>
    )
  }

  useEffect(() => {
    setChartData(currentChartData, dataState, tableIndex)
    if (currentChartData?.length > savedCharts?.length) {
      handleUpdate(true)
    }
  }, [currentChartData])

  function customHeaderComponent(value: string) {
    const currentIndex = primary.findIndex(
      (item: string) => item?.toLowerCase() === value?.toLowerCase()
    )

    let primaryValue = primary[currentIndex] || ''
    let secondaryValue = secondary[currentIndex] ? secondary[currentIndex] : ''
    let secondaryLabelValue = secondaryLabel ? secondaryLabel[currentIndex] : ''
    let primaryLabelValue = primaryLabel ? primaryLabel[currentIndex] : ''

    return {
      menuIcon: 'fa-bars',
      template: `<div class="ag-cell-label-container" role="presentation">
                    <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
                    <span ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
                    <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                        <span style="
                              padding-right: 10px; ">
                        <div>
                          <h6 style="
                              margin-bottom: 1px; font-size: 12px; font-weight: bold
                          ">${primaryLabelValue || primaryValue}</h6>
                          <p className='text-muted mb-0 fs-8' style="
                              margin-bottom: 0px; font-size: 10px; font-weight: bold
                          ">${secondaryLabelValue || secondaryValue}</p>
                        </div>
                        </span>
                        <span ref="eSortOrder" class="ag-header-icon ag-sort-order ag-hidden"></span>
                        <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon ag-hidden" style="color:#ff6666"></span>
                        <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon ag-hidden" style="color:#ff6666"></span>
                        <span ref="eSortMixed" class="ag-header-icon ag-sort-mixed-icon ag-hidden"></span>
                        <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon ag-hidden"></span>
                        <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                    </div>
                </div>`,
    }
  }

  function isDateValue(value: any): boolean {
    if (typeof value === 'number') {
      // Check if it's a plausible Excel serial date (adjust range as needed)
      return value > 40000 && value < 60000 // Dates between 1900 and 2100
    } else if (typeof value === 'string') {
      // Reject strings that are purely numbers
      if (!isNaN(+value)) {
        return false
      }
      // Check for ISO format (yyyy-mm-dd)
      const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/ // e.g., 2024-03-20
      if (isoDatePattern.test(value)) {
        const parsedDate = new Date(value)
        return !isNaN(parsedDate.getTime())
      }
      // Check for dd/mm/yyyy format
      const dmyDatePattern = /^\d{2}\/\d{2}\/\d{4}$/ // e.g., 20/03/2024
      if (dmyDatePattern.test(value)) {
        const [day, month, year] = value.split('/').map(Number)
        const parsedDate = new Date(year, month - 1, day) // JavaScript months are 0-based
        return (
          parsedDate.getDate() === day &&
          parsedDate.getMonth() === month - 1 &&
          parsedDate.getFullYear() === year
        )
      }
      return false // Reject other strings
    }
    return false
  }

  function getFilterType(value: string, isPrimary: boolean) {
    const currentIndex = isPrimary
      ? primary.findIndex((item: string) => item?.toLowerCase() === value?.toLowerCase())
      : secondary.findIndex((item: string) => item?.toLowerCase() === value?.toLowerCase())

    let sampleValue: any

    if (isPrimary) {
      const index =
        primary[currentIndex] &&
        data[0].findIndex(
          (item: string) => item?.toLowerCase() === primary[currentIndex]?.toLowerCase()
        )
      sampleValue = data[1] && data[1][index]
    } else {
      const primaryValue = primary[currentIndex]
      const convertedData = convertMedalData(data)
      if (convertedData && convertedData.length > 0) {
        sampleValue = convertedData[0][`${primaryValue}_secondary`]
      }
    }

    let filterType = 'agTextColumnFilter'
    if (isNumber(sampleValue)) {
      filterType = 'agNumberColumnFilter'
    } else if (isDate(sampleValue)) {
      filterType = 'agDateColumnFilter'
    } else if (isPrimary && currentIndex >= 0) {
      filterType = 'agTextColumnFilter'
    } else {
      filterType = 'agMultiColumnFilter'
    }
    return filterType
  }
  function CustomFilter(value: string) {
    const currentIndex = primary.findIndex(
      (item: string) => item?.toLowerCase() === value?.toLowerCase()
    )

    let primaryValue = primary[currentIndex] || ''
    let secondaryValue = secondary[currentIndex] ? secondary[currentIndex] : ''
    let secondaryLabelValue = secondaryLabel ? secondaryLabel[currentIndex] : ''
    let primaryLabelValue = primaryLabel ? primaryLabel[currentIndex] : ''

    let primaryFilterType = getFilterType(primaryValue, true)
    let secondaryFilterType = getFilterType(secondaryValue, false)
    const filter: { buttons: string[]; filters: any[] } = {
      buttons: ['reset'],
      filters: [
        {
          title: primaryLabelValue || primaryValue,
          display: 'subMenu',
          filter:
            primaryFilterType === 'agMultiColumnFilter' ? 'agTextColumnFilter' : primaryFilterType,
          buttons: ['reset'],
          filterValueGetter: (params: any) => params.value.name,
          filterParams: {
            buttons: ['reset'],
            valueGetter: (params: any) => params.getValue(primaryValue),
          },
        },
      ],
    }

    if (secondaryValue && secondaryValue !== '') {
      filter.filters.push({
        title: secondaryLabelValue || secondaryValue,
        display: 'subMenu',
        filter:
          secondaryFilterType === 'agMultiColumnFilter'
            ? 'agTextColumnFilter'
            : secondaryFilterType,
        buttons: ['reset'],
        filterValueGetter: (params: any) => params.value[`${primaryValue}_secondary`],
        filterParams: {
          buttons: ['reset'],
          valueGetter: (params: any) => params.getValue(`${primaryValue}_secondary`),
        },
      })
    }
    return filter
  }

  const MyLink = ({ url, children }: { url: string; children: string }) => {
    const normalizedUrl =
      url?.startsWith('http://') || url?.startsWith('https://') ? url : `https://${url}`
    return (
      <a
        href={normalizedUrl}
        target='_blank'
        style={{ color: '#150a6b', fontWeight: 'bold', textDecoration: 'underline' }}
      >
        {children}
      </a>
    )
  }

  useEffect(() => {
    const colDef =
      data.length > 1
        ? data[0].map((value: any, index: any) => {
            let filterType

            if (primary.map((item: string) => item.toLowerCase()).includes(value.toLowerCase())) {
              filterType = getFilterType(value, true)

              const currentIndex = primary.findIndex(
                (item: string) => item?.toLowerCase() === value?.toLowerCase()
              )

              let primaryValue = primary[currentIndex] || ''
              let secondaryValue = secondary[currentIndex] ? secondary[currentIndex] : ''
              return filterType === 'agDateColumnFilter'
                ? index === 0
                  ? {
                      field: value,
                      width: 150,
                      chartDataType: 'category',
                      filter: filterType,
                      minWidth: 300,
                      menuTabs: ['filterMenuTab'],
                      cellStyle: { 'padding-top': '10px', 'line-height': '20px' },
                      filterParams: CustomFilter(value),
                      headerComponentParams: customHeaderComponent(value),
                      cellRenderer: CustomRowData,
                      tooltipComponent: CustomTooltip,
                      tooltipValueGetter: (params: any) => {
                        const columnKey = params.colDef.field
                        const match = columnKey.match(/\((.*?)\)/)
                        return match ? 'show-tooltip' : null
                      },
                    }
                  : {
                      field: value,
                      chartDataType: 'series',
                      filter: filterType,
                      minWidth: 300,
                      menuTabs: ['filterMenuTab'],
                      cellStyle: { 'padding-top': '10px', 'line-height': '20px' },
                      filterParams: CustomFilter(value),
                      headerComponentParams: customHeaderComponent(value),
                      cellRenderer: CustomRowData,
                      tooltipComponent: CustomTooltip,
                      tooltipValueGetter: (params: any) => {
                        const columnKey = params.colDef.field
                        const match = columnKey.match(/\((.*?)\)/)
                        return match ? 'show-tooltip' : null
                      },
                    }
                : filterType === 'agMultiColumnFilter'
                ? {
                    field: value,
                    width: 150,
                    chartDataType: 'category',
                    filter:
                      secondaryValue && secondaryValue !== '' ? 'agMultiColumnFilter' : filterType,
                    minWidth: 300,
                    filterParams: CustomFilter(value),
                    headerComponentParams: customHeaderComponent(value),
                    cellStyle: { 'padding-top': '10px', 'line-height': '20px' },
                    menuTabs: ['filterMenuTab'],
                    cellRenderer: CustomRowData,
                    tooltipComponent: CustomTooltip,
                    tooltipValueGetter: (params: any) => {
                      const columnKey = params.colDef.field
                      const match = columnKey.match(/\((.*?)\)/)
                      return match ? 'show-tooltip' : null
                    },
                  }
                : filterType === 'agTextColumnFilter'
                ? {
                    field: value,
                    width: 150,
                    chartDataType: 'category',
                    filter:
                      secondaryValue && secondaryValue !== '' ? 'agMultiColumnFilter' : filterType,
                    minWidth: 300,
                    filterParams: CustomFilter(value),
                    headerComponentParams: customHeaderComponent(value),
                    cellStyle: { 'padding-top': '10px', 'line-height': '20px' },
                    menuTabs: ['filterMenuTab'],
                    cellRenderer: CustomRowData,
                    tooltipComponent: CustomTooltip,
                    tooltipValueGetter: (params: any) => {
                      const columnKey = params.colDef.field
                      const match = columnKey.match(/\((.*?)\)/)
                      return match ? 'show-tooltip' : null
                    },
                  }
                : {
                    field: value,
                    chartDataType: 'series',
                    filter:
                      secondaryValue && secondaryValue !== '' ? 'agMultiColumnFilter' : filterType,
                    minWidth: 300,
                    filterParams: CustomFilter(value),
                    headerComponentParams: customHeaderComponent(value),
                    cellStyle: { 'padding-top': '10px', 'line-height': '20px' },
                    menuTabs: ['filterMenuTab'],
                    cellRenderer: CustomRowData,
                    tooltipComponent: CustomTooltip,
                    tooltipValueGetter: (params: any) => {
                      const columnKey = params.colDef.field
                      const match = columnKey.match(/\((.*?)\)/)
                      return match ? 'show-tooltip' : null
                    },
                  }
            }
          })
        : []

    setColumnDefs(colDef.filter((item: any) => item !== undefined))
    const convertedData = convertMedalData(data)
    setRowData(convertedData)

    // Update filtered row count when data changes
    setTimeout(() => {
      if (gridRef.current?.api) {
        setFilteredRowCount(gridRef.current.api.getDisplayedRowCount())
      }
    }, 100)
  }, [data])

  function comparator(dateFromFilter: Date, cellValue: any) {
    if (cellValue == null) {
      return 0
    }
    const dateParts = cellValue?.split('-')
    const day = Number(dateParts[2])
    const month = Number(dateParts[1]) - 1
    const year = Number(dateParts[0])
    const cellDate = new Date(year, month, day)
    if (cellDate < dateFromFilter) {
      return -1
    } else if (cellDate > dateFromFilter) {
      return 1
    }
    return 0
  }

  function convertMedalData(data: any) {
    const headers = data[0]
    const result = []

    for (let i = 1; i < data.length; i++) {
      const row = data[i]
      const rowData: any = {}

      for (let j = 0; j < headers.length; j++) {
        if (
          secondary
            .map((item: string, index: number) => item?.toLowerCase())
            .includes(headers[j].toLowerCase())
        ) {
          const currentIndex = secondary.findIndex(
            (item: string) => item?.toLowerCase() === headers[j].toLowerCase()
          )
          const primaryValue = primary[currentIndex]
          const secondaryLabel = secondary[currentIndex]
          rowData[`${primaryValue}_secondary`] = isNumber(row[j]) ? Number(row[j]) : row[j]
          rowData[`${primaryValue}_secondary_label`] = secondaryLabel
        } else rowData[headers[j]] = isNumber(row[j]) ? Number(row[j]) : row[j]
      }

      result.push(rowData)
    }

    return result
  }

  function UpdateChartTitle(title: string, chartIndex: number) {
    const updatedChart = currentChartData.map((chart: any, index: number) => {
      if (index + 1 === chartIndex) {
        return { ...chart, chartTitle: title, myChartId: title }
      }
      return chart
    })
    if (updatedChart.length === currentChartData.length) {
      setDataState(DynamicAction.UPDATE)

      setCurrentChartData((charts: any[]) => {
        const isTitleOccupied = charts.some(
          (chart, i) => chart.myChartId === title && i + 1 !== chartIndex
        )
        if (isTitleOccupied) {
          Toast(`Title already occupied by another chart`, ToastType.error)
          return charts
        } else
          return charts.map((chart: any, index: number) => {
            if (index + 1 === chartIndex) {
              return { ...chart, chartTitle: title, myChartId: title }
            }
            return chart
          })
      })
    }
  }

  const showNoRowsOverlay = () => (
    <div className='row justify-content-center  text-center py-8 m-6'>
      <img
        alt='no items'
        src={toAbsoluteUrl('/media/custom-images/emptyRecentCompanies.svg')}
        className='h-150px my-10 w-350 justify-content-center'
      />
      <p className='fs-base text-muted text-center'>
        No Data found. Please check your uploaded files and try again
      </p>
    </div>
  )

  const componentsRegistry = useMemo(() => {
    const headers = (rowData && rowData[0]) || []
    return Object.keys(headers).reduce((acc: any, key) => {
      acc[key] = memo(CustomRowData)
      return acc
    }, {})
  }, [])

  const openToolPanel = (key: any) => {
    if (key === 'columns') {
      if (gridRef) {
        if (!isColumnOpen) gridRef?.current?.api?.openToolPanel(key)
        else gridRef?.current?.api?.closeToolPanel()
        setIsColumnOpen((state) => !state)
        setIsFilterOpen(false)
      }
    } else if (key === 'filters') {
      if (gridRef) {
        if (!isFilterOpen) {
          gridRef?.current?.api?.openToolPanel(key)
        } else gridRef?.current?.api?.closeToolPanel()
        setIsFilterOpen((state) => !state)
        setIsColumnOpen(false)
      }
    }
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ['reset'],
        maxNumConditions: 5,
      },
      filter: true,
      flex: 1,
      resizable: true,
    }),
    []
  )

  function LinkOrUnlinkCharts(currentChartId: string, chartRef: any) {
    setDataState(DynamicAction.UPDATE)
    setCurrentChartData((charts: any[]) =>
      charts.map((chart: any, index: number) => {
        if (chart.chartId === currentChartId) {
          return {
            ...chart,
            unlinkChart: !currentChartData[index]?.unlinkChart || !chart.unlinkChart,
          }
        }
        return chart
      })
    )
  }
  function updateState() {
    const chartWrap = document.querySelectorAll('.chart-wrapper')
    if (chartWrap.length >= 1) {
      chartWrap.forEach((element) => {
        if (chartWrap.length === 1) {
          element.classList.remove('col-12', 'col-md-6')
        } else element.classList.add('col-12', 'col-md-6')
      })
    }
  }

  const createChartContainer = useCallback((chartRef: ChartRef): void => {
    const eChart = chartRef.chartElement
    const generatedCharts = gridRef.current.api.getChartModels() || []
    const updatedCharts = generatedCharts.map((chart: any, index: number) => {
      if (chart['chartTitle'] === undefined) {
        return {
          ...chart,
          chartTitle: currentChartData[index]?.chartTitle || `${tableTitle[0]} Chart ${index + 1}`,
          // chartId: currentChartData[index]?.chartId || chart.chartId,
          chartIndex: tableIndex,
          myChartId: currentChartData[index]?.chartTitle || `${tableTitle[0]} Chart ${index + 1}`,
          unlinkChart: currentChartData[index]?.chartTitle
            ? currentChartData[index]?.unlinkChart
            : true,
        }
      } else {
        return chart
      }
    })
    setDataState(DynamicAction.ADD)
    setCurrentChartData(updatedCharts)
    const themeName =
      document.documentElement?.getAttribute('data-default-theme') || 'ag-theme-material'
    const eParent = document.querySelector('#container') as HTMLElement
    if (!eParent) {
      console.error('Container not found!')
      return
    }

    function getTitle() {
      const matchingChart = updatedCharts.find((chart: any) => chart.chartId === chartRef.chartId)
      return matchingChart ? matchingChart.chartTitle : ''
    }

    const chartWrapperHTML = `
        <div class="chart-wrapper ${themeName} pb-6 px-4 mb-3" >
            <div class="chart-wrapper-top d-flex justify-content-between align-items-center bg-white rounded-top p-6">
            <div class="d-flex align-items-center">
                <h2 id="editableTitle" contenteditable="true" class="chart-wrapper-title mb-0" data-tooltip="Double click to edit"> ${getTitle()}</h2>
                </div>
                <button class="chart-wrapper-close btn-close rounded-bottom" aria-label="Close"></button>
            </div>
            <div class="chart-wrapper-body rounded"></div>
        </div>
    `

    eParent.insertAdjacentHTML('beforeend', chartWrapperHTML)
    const eChartWrapper = eParent.lastElementChild as HTMLElement

    // Append the chart to the wrapper
    eChartWrapper.querySelector('.chart-wrapper-body')!.append(eChart)

    // Add event listener for close button
    eChartWrapper.querySelector('.chart-wrapper-close')!.addEventListener('click', () => {
      setShowClose(true)
      setCurrentChartRef(chartRef)
      setEEParent(eParent)
      setEEWrapper(eChartWrapper)
    })

    // Add event listener for link chart button
    eChartWrapper
      .querySelector('.ag-icon.ag-chart-menu-icon.ag-icon-linked')
      ?.addEventListener('click', (e) => {
        LinkOrUnlinkCharts(chartRef.chartId, chartRef)
      })

    // Add event listener for unlink chart button
    eChartWrapper
      .querySelector('.ag-icon.ag-chart-menu-icon.ag-icon-unlinked')
      ?.addEventListener('click', (e) => {
        LinkOrUnlinkCharts(chartRef.chartId, chartRef)
      })

    const titleElement = eChartWrapper.querySelector('.chart-wrapper-title') as HTMLElement

    titleElement.addEventListener('blur', () => {
      const updatedTitle = titleElement.innerText.trim()
      UpdateChartTitle(updatedTitle, updatedCharts.length)
    })

    updateState()
  }, [])

  const generateChart = useCallback((data: any) => {
    if (gridRef.current.api && data) gridRef.current.api.createRangeChart(data)
  }, [])

  useEffect(() => {
    currentChartData &&
      currentChartData.map((data: any) => {
        if (data) {
          return generateChart(data)
        }
      })
  }, [gridRef.current?.api])

  function onChartOptionsChanged(event: ChartOptionsChanged) {
    const currentChart = currentChartData.find((chart) => chart.chartId === event.chartId)

    if (currentChart) {
      const updatedState = currentChartData.map((chart) =>
        chart.chartId === event.chartId ? { ...chart, chartType: event.chartType } : chart
      )
      handleUpdate(true)
      setDataState(DynamicAction.UPDATE)
      setCurrentChartData(updatedState)
    }
  }

  function onChartRangeSelectionChanged(event: ChartRangeSelectionChanged) {
    const currentChart = currentChartData.find((chart) => chart.chartId === event.chartId)

    if (currentChart) {
      const updatedState = currentChartData.map((chart) =>
        chart.chartId === event.chartId ? { ...chart, cellRange: event.cellRange } : chart
      )
      handleUpdate(true)
      setDataState(DynamicAction.UPDATE)
      setCurrentChartData(updatedState)
    }
  }

  function onChartDestroyed(event: ChartDestroyed) {
    const updatedChartData = currentChartData.filter(
      (chartData) => chartData.chartId !== event.chartId
    )
    handleUpdate(true)
    setDataState(DynamicAction.DELETE)
    setCurrentChartData(updatedChartData)
  }

  return (
    <>
      <div
        className={`card p-0 ${!isExpanded ? 'h-50px' : 'h-625px'} `}
        style={{
          boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
          border: '1',
          borderColor: '#F1F1F2',
        }}
      >
        <div
          className={`border-0 ${isExpanded ? 'p-8' : 'pt-4 px-9'} pb-8 content`}
          style={{ overflowY: 'hidden', overflowX: 'auto', scrollbarWidth: 'none' }}
        >
          <div className='row align-items-center'>
            {/* Left Side: Title */}
            <div className='col-6'>
              <details onToggle={handleToggle} open>
                <summary
                  className='d-flex align-items-center'
                  onClick={(e) => {
                    const target = e.target as HTMLElement
                    if (
                      target.closest('[data-kt-menu-trigger]') ||
                      target.closest('[data-kt-menu="true"]')
                    ) {
                      e.preventDefault()
                      return false
                    }
                  }}
                >
                  {isExpanded ? (
                    <i className='bi bi-chevron-double-down fw-bold me-2' />
                  ) : (
                    <i className='bi bi-chevron-double-right fw-bold me-2' />
                  )}

                  <div className='card-title d-flex flex-column'>
                    <h1 className='mb-0'>{tableTitle[0]}</h1>
                    {isExpanded && (
                      <p className='fs-7 text-gray-400 mb-0'>
                        {gridRef?.current?.api
                          ? `${filteredRowCount} Entries`
                          : `${data.length > 0 ? data.length - 1 : 0} Entries`}
                      </p>
                    )}
                  </div>
                </summary>
              </details>
            </div>

            {isExpanded && (
              <div className='col-6 d-flex justify-content-end'>
                <input
                  type='search'
                  className='form-control custom-input fw-bold shadow-lg h-38px mw-250px'
                  name='search'
                  placeholder='Search...'
                  value={searchedValue}
                  onChange={(e: any) => {
                    setSearchedValue(e.target.value)
                    gridRef.current.api.setQuickFilter(e.target.value)
                  }}
                />

                <button
                  type='button'
                  value='Columns'
                  onClick={() => openToolPanel('columns')}
                  className={`px-4 rounded border border-1 border-secondary-txt d-flex flex-row align-items-center h-38px fw-normal ms-4 ${
                    isColumnOpen ? 'bg-growth-card text-white' : 'bg-transparent'
                  }`}
                >
                  <KTSVG
                    path='/media/icons/duotune/layouts/lay004.svg'
                    className={`svg-icon-3 ${isColumnOpen ? 'text-white' : 'text-secondary-txt'}`}
                  />
                  Columns
                </button>

                <button
                  value='Filters'
                  onClick={() => openToolPanel('filters')}
                  className={`px-4 rounded border border-1 border-secondary-txt d-flex flex-row align-items-center h-38px fw-normal ms-4 ${
                    isFilterOpen ? 'bg-growth-card text-white' : 'bg-transparent'
                  }`}
                >
                  <KTSVG
                    path='/media/icons/duotune/general/gen031.svg'
                    className={`svg-icon-3 ${isFilterOpen ? 'text-white' : 'text-secondary-txt'}`}
                  />
                  Filter
                </button>

                {tableId ? (
                  <CustomFilterComponent
                    currentFilterState={gridRef?.current?.api?.getFilterModel()}
                    onFilterSelect={(filter: any) => onFilterSelected(filter)}
                    filters={filterData || []}
                    saveFilter={(values: DynamicTableFilterInput) => saveFilter(values)}
                    removeFilter={(values: GetDynamicFilterInput) => removeFilter(values)}
                    tableName={tableTitle[0]}
                    tableId={tableId}
                  />
                ) : (
                  <></>
                )}
              </div>
            )}
          </div>
        </div>

        {showClose && (
          <Modal
            id='kt_modal_add_inv'
            tabIndex={-1}
            aria-hidden='true'
            dialogClassName='modal-dialog modal-dialog-centered mw-400px '
            show={showClose}
            onHide={() => setShowClose(false)}
          >
            <Modal.Body className='p-9'>
              <div className='card'>
                <div className='card-body p-0'>
                  <div className='fs-5 pb-2 fw-bold'>
                    Are you sure you want to remove this chart?
                  </div>
                  <div className='fs-7 fw-normal pb-2'>
                    <b>Note : </b> Clicking this will delete the chart from the container.
                  </div>

                  <div className='d-flex col justify-content-between pt-6'>
                    <button
                      className='px-4 rounded border border-1 border-primary bg-transparent h-38px fw-normal text-primary'
                      onClick={() => setShowClose(false)}
                    >
                      <span className='indicator-label'>Cancel</span>
                    </button>
                    <button
                      type='submit'
                      className='px-4 rounded border border-0 border-primary bg-primary h-38px fw-normal text-white'
                      onClick={() => {
                        setDataState(DynamicAction.DELETE)
                        currentChartRef.destroyChart()
                        eeParent.removeChild(eeWrapper)
                        updateState()
                        setShowClose(false)
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        )}

        <div
          className='card-body w-100 px-9 pt-0 overflow-auto h-100 content'
          style={{ display: isExpanded ? 'block' : 'none' }}
        >
          <div style={containerStyle} className='ag-theme-material'>
            <style>
              {`
                    .ag-theme-material .ag-side-bar {
                      
                      ${
                        isColumnOpen || isFilterOpen
                          ? 'border: 1px solid #e4e6ef; border-radius: 4px;'
                          : ''
                      }
                    }
                    .ag-theme-material .ag-root-wrapper-body {
                      gap: 8px;
                    }
                    .ag-theme-material .ag-root.ag-unselectable.ag-layout-normal {
                      ${'border: 1px solid #e4e6ef; border-radius: 4px;'}
                    }
                  `}
            </style>
            <div className='d-flex flex-wrap justify-content-between'>
              <div style={gridStyle} className=''>
                <AgGridReact
                  sideBar={dynamicSideBar}
                  onGridReady={(params) => {
                    gridRef.current = params
                    // Initialize filtered row count
                    setFilteredRowCount(params.api.getDisplayedRowCount())
                  }}
                  onFilterChanged={() => {
                    if (gridRef.current?.api) {
                      const filterModel = gridRef.current.api.getFilterModel()
                      const hasActiveFilters = Object.keys(filterModel).length > 0

                      // Update filtered row count
                      setFilteredRowCount(gridRef.current.api.getDisplayedRowCount())

                      if (hasActiveFilters && !isFilterOpen) {
                        gridRef.current.api.openToolPanel('filters')
                        setIsFilterOpen(true)
                        setIsColumnOpen(false)
                      }
                    }
                  }}
                  ref={gridRef}
                  components={componentsRegistry}
                  alwaysShowHorizontalScroll
                  alwaysMultiSort
                  animateRows={true}
                  defaultColDef={defaultColDef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  pagination={false}
                  enableCharts={true}
                  enableRangeSelection={true}
                  suppressRowTransform={true}
                  suppressCopyRowsToClipboard={true}
                  suppressCopySingleCellRanges={true}
                  suppressCellFocus={true}
                  suppressMenuHide={false}
                  tooltipShowDelay={50}
                  tooltipInteraction={true}
                  enableBrowserTooltips={false}
                  noRowsOverlayComponent={showNoRowsOverlay}
                  createChartContainer={createChartContainer}
                  onChartOptionsChanged={onChartOptionsChanged}
                  onChartRangeSelectionChanged={onChartRangeSelectionChanged}
                  onChartDestroyed={onChartDestroyed}
                  rowClass={'border-right-0 border-left-0 border-dashed'}
                  overlayNoRowsTemplate={
                    '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
                  }
                  getRowStyle={(params) => {
                    if (params.rowIndex % 2 === 0) {
                      return { background: '#f8f8f8' }
                    } else {
                      return { background: '#ffffff' }
                    }
                  }}
                ></AgGridReact>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
